import React, { useContext, useEffect, useState } from 'react';
import { InsidePageContainer, PageContainer } from '../common/style';
import Header from '../common/Header';
import { Stack, useMediaQuery, useTheme } from '@mui/material';
import { useTicketsStore } from '../store/ticketsStore';
import { TicketsCart } from '../common';
import { LoaderCountContext } from 'context/LoaderV5';

import RecapContainer from './sections/RecapContainer';
import { useHistory } from 'react-router-dom';
import { useNetwork } from 'services/network';
import { discountCodeStatus, Roles } from 'utils/constants';
import { format } from 'date-fns';
import {
  dataLayerPagesEnum,
  parseProductForDataLayer,
  setECommerceDataLayer,
  setVirtualPageDataLayer,
} from 'utils/dataLayerManager';
import { useIntl } from 'react-intl';
import translations from 'utils/translations';

const Recap = ({ locale, loggedUser }) => {
  const theme = useTheme();
  const isLgDown = useMediaQuery(theme.breakpoints.down('lg'));
  const {
    productsInCart,
    ticketsData,
    merchInCart,
    submittedDiscountCodesWithEmptySpaces,
  } = useTicketsStore();
  const history = useHistory();
  const network = useNetwork();
  const { setLoaderActive } = useContext(LoaderCountContext);
  const showLoader = () => setLoaderActive(true);
  const hideLoader = () => setLoaderActive(false);
  const [customer, setCustomer] = useState(null);
  const [conditionsOfSales, setConditionsOfSales] = useState({
    one: false,
    two: false,
  });
  const intl = useIntl();
  const i18nRecap = translations.TicketsV2(intl).Recap;
  const i18nCommon = translations.TicketsV2(intl).Common;
  const i18nDiscount = translations.DiscountCode(intl);

  const closeAlert = (sev) =>
    useTicketsStore.setState({ alertData: { severity: sev } });

  useEffect(() => {
    const fetchCustomer = async () => {
      const { customerData } = await network.salesforceCustomerLambda({
        federationId: loggedUser?.salId,
      });
      if (customerData?.accountid) setCustomer(customerData);
    };
    if (loggedUser?.salId && !customer) fetchCustomer();
  }, [loggedUser]);

  useEffect(() => {
    setVirtualPageDataLayer(dataLayerPagesEnum.TICKETS_RECAP);
  }, []);

  const checkoutDisabled = !conditionsOfSales.one || !conditionsOfSales.two;

  const handleBack = () =>
    history.push(ticketsData.length ? '/assign-ticket' : '/');

  const createDevelonOrder = async (params) => {
    showLoader();
    const data = await network.ticketingLambda(params);
    hideLoader();
    return data.number;
  };

  const getMerchName = (merch) =>
    merch?.description_map?.[locale] ||
    merch?.description_map?.it ||
    merch?.description ||
    '';

  const handlePayNow = async () => {
    if (checkoutDisabled) return null;
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
    const now = new Date();
    const isoDate = format(now, 'yyyy-MM-dd hh:MM:ss');
    const merchItems = merchInCart
      .filter((merch) => merch.quantity > 0 && !!merch.isMerchandising)
      .map((i) => ({
        note: getMerchName(i),
        quantity: i.quantity,
        product_code: i.code,
      }));
    if (ticketsData?.length) {
      const codes = submittedDiscountCodesWithEmptySpaces?.filter(
        (code) => !!code
      );
      if (codes.length && loggedUser) {
        showLoader();
        // CHECK CODES!!
        const input = {
          codes,
          year: `${new Date().getFullYear()}`,
          operatorName: loggedUser.email || '',
          operatorSalesforceId: loggedUser.salId || '',
        };
        const res = await network.checkCodesBeforeOrderLambda(input);
        const title = i18nDiscount.Common.warning;
        if (res?.status) {
          switch (res.status) {
            case discountCodeStatus.CODE_EXPIRED:
              useTicketsStore.setState({
                alertData: {
                  title,
                  message: (
                    <span>
                      {i18nDiscount.Common.specificCode.code}{' '}
                      {res.code ? (
                        <strong>{res.code}</strong>
                      ) : (
                        i18nDiscount.Common.specificCode.used
                      )}{' '}
                      {i18nDiscount.Common.specificCode.expired}
                    </span>
                  ),
                  onClose: () => closeAlert('error'),
                  severity: 'error',
                },
              });
              break;
            case discountCodeStatus.CODE_NOT_FOUND:
              useTicketsStore.setState({
                alertData: {
                  title,
                  message: (
                    <span>
                      {i18nDiscount.Common.specificCode.code}{' '}
                      {res.code ? (
                        <strong>{res.code}</strong>
                      ) : (
                        i18nDiscount.Common.specificCode.used
                      )}{' '}
                      {i18nDiscount.Common.specificCode.notValid}
                    </span>
                  ),
                  onClose: () => closeAlert('error'),
                  severity: 'error',
                },
              });
              break;
            default:
              break;
          }
          if (res.status !== discountCodeStatus.CODE_UPDATED) {
            return hideLoader();
          }
        } else {
          hideLoader();
          return useTicketsStore.setState({
            alertData: {
              title,
              message: <span>{i18nDiscount.Common.verifyCodeError}</span>,
              onClose: () => closeAlert('error'),
              severity: 'error',
            },
          });
        }
      }
    }
    const ownersItem = ticketsData.map(({ assignData, ticketData }) => ({
      note: ticketData.description,
      quantity: 1,
      product_code: ticketData.code,
      owners: [
        {
          firstname: assignData.firstname,
          lastname: assignData.lastname,
          email: assignData.email,
          locale: locale,
          phone: assignData?.phone || '',
          country:
            assignData.country?.value?.toUpperCase() ||
            assignData.country?.toUpperCase() ||
            'ITA',
          send_ticket_to_owner: assignData?.sendToThisEmail || false,
          ...(assignData?.district && { district: assignData.district }),
          city: assignData?.city?.value || assignData?.city || '',
          ...(assignData?.company && { sdm_firm_name: assignData.company }),
          sdm_operator_type: assignData.sdm_operator_type,
          ...(assignData.sdm_operator_sub_type && {
            sdm_operator_sub_type: assignData.sdm_operator_sub_type,
          }),
          ...(assignData.sdm_other_type && {
            sdm_other_type: assignData.sdm_other_type,
          }),
          ...(assignData.sdm_role_in_company && {
            sdm_role_in_company: assignData.sdm_role_in_company,
          }),
          ...(assignData.sdm_other_role_in_company && {
            sdm_other_role_in_company: assignData.sdm_other_role_in_company,
          }),
          flag_privacy_1: {
            version: '1',
            date: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
            code: 'B7A_GUEST_INTESTA_PRIVACY',
            value: assignData.owner_flag_privacy_1 === '1',
          },
          flag_privacy_2: {
            version: '1',
            date: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
            code: 'B7A_GUEST_INTESTA_PRIVACY',
            value: assignData.owner_flag_privacy_2 === '1',
          },
          flag_privacy_3: {
            version: '1',
            date: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
            code: 'B7A_GUEST_INTESTA_PRIVACY',
            value: assignData.owner_flag_privacy_3 === '1',
          },
        },
      ],

      reservations: assignData.reservations
        .filter((r) => r.selected)
        .map((r) => ({
          area_code: r.areaCode,
          group_code: r.groupCodes?.[0],
          reservation_date: r.date,
        })),
    }));

    const itemsArray = [...ownersItem, ...merchItems];

    const input = {
      params: '/orders/prepare',
      customMethod: 'post',
      data: {
        customer: {
          customer_type:
            loggedUser.type === Roles.OPERATOR ? 'operator' : 'end_user',
          email: customer?.personEmail || loggedUser.email || '',
          firstname: customer?.firstname || loggedUser.givenName || '',
          lastname: customer?.lastname || loggedUser.familyName || '',
          locale,
          mobile: '',
          country: customer?.personMailingCountry?.toUpperCase() || 'ITA',
          code: loggedUser?.salId,
          flag_privacy_1: {
            version: '1',
            date: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
            code: 'B7A_GUEST_INTESTA_PRIVACY',
            value: true,
          },
          flag_privacy_2: {
            version: '1',
            date: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
            code: 'B7A_GUEST_INTESTA_PRIVACY',
            value: customer?.flagMarketing || false,
          },
          flag_privacy_3: {
            version: '1',
            date: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
            code: 'B7A_GUEST_INTESTA_PRIVACY',
            value: customer?.flagProfilazione || false,
          },
        },
        items: itemsArray,
        order: {
          invoice_datetime: isoDate,
          presales_amount: 0,
          billing_method: 'cashsales',
          locale,
        },
        support: 'digital',
      },
    };
    const _orderNumber = await createDevelonOrder({
      params: '/orders/prepare',
      customMethod: 'post',
      data: input,
      federationId: loggedUser?.salId,
    });

    if (_orderNumber) {
      const dataLayerItems = [];
      input.data.items.map((i, idx) => {
        const code = i.product_code;
        const parsedMerch = merchInCart?.map((merch) => ({
          ticketData: merch,
        }));
        const product = [...ticketsData, ...parsedMerch].find(
          (p) => p.ticketData.code === code
        )?.ticketData;
        if (product && product?.id) {
          dataLayerItems.push(
            parseProductForDataLayer({
              product,
              idx,
              isMerch: product.product_type === 'merchandising',
              quantity: i.quantity,
            })
          );
        }
      });
      if (dataLayerItems?.length) {
        setECommerceDataLayer({ items: dataLayerItems, event: 'add_to_cart' });
      }
      history.push(`/checkout`, { order_number: _orderNumber });
    }
  };

  const isCartOpen =
    productsInCart?.length &&
    productsInCart?.some((pro) => pro.quantity && pro.quantity > 0);
  const isApp = isCartOpen && window.location.pathname.includes('app');

  return (
    <PageContainer direction={'column'} forcePaddingBottom={isApp}>
      <InsidePageContainer>
        <Header backFn={handleBack} loggedUser={loggedUser} />
        <Stack direction={isLgDown ? 'column' : 'row'}>
          {ticketsData?.length ? (
            <RecapContainer
              loggedUser={loggedUser}
              conditionsOfSales={conditionsOfSales}
              setConditionsOfSales={setConditionsOfSales}
              recapMessageTexts={i18nRecap}
              locale={locale}
            />
          ) : (
            <Stack
              style={{
                width: isLgDown ? '100%' : '64%',
                ...(!isLgDown && {
                  paddingRight: '70px',
                }),
              }}
              gap={'25px'}
            ></Stack>
          )}
          {isCartOpen ? (
            <TicketsCart
              locale={locale}
              procideFn={handlePayNow}
              procideMsg={i18nCommon.proceedMsg}
              btnDisabled={checkoutDisabled}
            />
          ) : null}
        </Stack>
      </InsidePageContainer>
    </PageContainer>
  );
};
export default Recap;
